import { useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Modal } from 'components/UI/Modal/Modal'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import {
  ChainSearchResult,
  DoorSearchResult,
  useDoorsAndChainsSearch,
} from 'features/elastic/searchers'
import { useForm } from 'react-hook-form'

interface ManualSearchModalProps {
  show: boolean
  onClose: () => void
  onSelect: (
    door: DoorSearchResult | null,
    chain: ChainSearchResult | null
  ) => void
}

export function ManualSearchModal({
  show,
  onClose,
  onSelect,
}: ManualSearchModalProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 500)
  const { data: searchResults, isLoading } =
    useDoorsAndChainsSearch(debouncedSearch)
  const { control } = useForm()

  const doors = searchResults?.doors || []
  const chains = searchResults?.chains || []

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onClose()}
      title="Manual Search"
      description="Search for a door or chain to match"
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <TextInput
          name="search"
          control={control}
          placeholder="Search for a door or chain..."
          onChange={(value) => setSearch(value)}
        />
        <div className="h-400px">
          {isLoading ? (
            <div className="flex items-center justify-center p-4 h-[416px]">
              Loading...
            </div>
          ) : doors.length === 0 && chains.length === 0 ? (
            <div className="flex items-center justify-center p-4">
              No results found
            </div>
          ) : (
            <div className="flex flex-row gap-4">
              {chains.length > 0 && (
                <div className="flex flex-col gap-2 flex-1">
                  <div className="font-bold text-sm text-gray-500">Chains</div>
                  <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                    {chains.map((chain) => (
                      <div
                        key={chain.item_id}
                        className="flex items-center justify-between p-2 border rounded cursor-pointer hover:bg-gray-50"
                        onClick={() => onSelect(null, chain)}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="font-bold">{chain.name}</div>
                          <div className="text-xs">{chain.count} locations</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {doors.length > 0 && (
                <div className="flex flex-col gap-2 flex-1">
                  <div className="font-bold text-sm text-gray-500">Doors</div>
                  <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                    {doors.map((door) => (
                      <div
                        key={door.item_id}
                        className="flex items-center justify-between p-2 border rounded cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          const chain = chains.find(
                            (c) => c.item_id === door.chain_id
                          )
                          onSelect(door, chain ?? null)
                        }}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="font-bold">{door.name}</div>
                          <div className="text-xs flex flex-col gap-1">
                            <div>
                              {door.score} - {door.address}
                            </div>
                            <div>
                              {door.city}, {door.state} {door.zipcode}{' '}
                              {door.country}
                            </div>
                            {door.domain && <div>Domain: {door.domain}</div>}
                            <div>
                              Chain: {door.chain_name} ({door.chain_size}{' '}
                              locations)
                            </div>
                            {door.type && <div>Type: {door.type}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
